import {
  type OauthAsk,
  type OauthConsent,
  oauthConsentSchema,
  type OauthDecided,
  oauthDecidedSchema,
  type OauthDecision,
} from "@crux/contracts";
import { z } from "zod";
import { problem } from "./errors.js";

/**
 * The consent screen's two calls (DESIGN.md "Sign in with Crux"). Like the AI
 * apps page's, these routes exist only in the Rust API, so they are plain
 * requests parsed with the shared schemas in `@crux/contracts`.
 */

const refusalSchema = z.object({ message: z.string() });

/** A refusal in the server's own words: they say what was wrong with the link. */
async function refused(response: Response, attempt: string): Promise<Error> {
  if (response.status === 400 || response.status === 403) {
    const parsed = refusalSchema.safeParse(await response.json().catch(() => null));
    if (parsed.success) return new Error(parsed.data.message);
  }
  return new Error(problem(response.status, attempt));
}

/** What the screen shows for the app's ask, sent as the app's own query string. */
export async function fetchOauthConsent(search: string): Promise<OauthConsent> {
  const response = await fetch(`/api/oauth/authorize${search}`, {
    credentials: "same-origin",
  });
  if (!response.ok) throw await refused(response, "open the sign-in");
  return oauthConsentSchema.parse(await response.json());
}

/** The person's decision; where their browser goes next. */
export async function decideOauth(
  ask: OauthAsk,
  decision: OauthDecision,
): Promise<OauthDecided> {
  const response = await fetch("/api/oauth/authorize", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ ask, decision }),
  });
  if (!response.ok) throw await refused(response, "connect the app");
  return oauthDecidedSchema.parse(await response.json());
}

/**
 * The ask, read from the query string the app sent the person here with. A
 * missing field is sent as empty, for the server to refuse in its own words.
 */
export function askFrom(params: URLSearchParams): OauthAsk {
  const state = params.get("state");
  const resource = params.get("resource");
  return {
    clientId: params.get("client_id") ?? "",
    redirectUri: params.get("redirect_uri") ?? "",
    responseType: params.get("response_type") ?? "",
    codeChallenge: params.get("code_challenge") ?? "",
    codeChallengeMethod: params.get("code_challenge_method") ?? "",
    ...(state === null ? {} : { state }),
    ...(resource === null ? {} : { resource }),
  };
}
